/**
 * Meta pixel — the browser half. The server half is
 * functions/api/_meta.ts (Conversions API from the Paddle webhook).
 *
 * What it reports, and nothing more:
 *   - PageView              — every screen (the pixel follows pushState itself)
 *   - CompleteRegistration  — a NEW account's first sign-in on this device
 *   - StartTrial / Purchase — Paddle's checkout.completed, with the same event
 *                             id the webhook uses so Meta counts it once
 *
 * No email, no name, no user id goes to Meta from here (CLAUDE.md: no user
 * data to analytics). What the server needs to match a payment to an ad
 * click — the `_fbp` / `_fbc` cookies and the user agent — rides along in
 * Paddle's custom_data (see metaCheckoutData).
 *
 * Only on app.fluent.ge, and only with VITE_META_PIXEL_ID set: localhost,
 * preview deploys, e2e runs and the dormant native shell never load it.
 */

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const PROD_HOST = 'app.fluent.ge';
const SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';
/** Fallback for `_fbc` when an ad link landed straight on the app. */
const FBC_KEY = 'fluent.meta.fbc';
/** Users this device has already reported as registered. */
const REG_KEY = 'fluent.meta.registered';
/** A sign-in counts as a registration only this soon after the account was made. */
const NEW_ACCOUNT_MS = 24 * 60 * 60 * 1000;

let enabled = false;

/**
 * Meta's standard base code, minus the inline <script>: a queueing stub, so
 * calls made before fbevents.js arrives are replayed in order.
 */
function installStub(): Fbq {
  if (window.fbq) return window.fbq;
  const n = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod(...args);
    else n.queue.push(args);
  } as Fbq;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  window.fbq = n;
  window._fbq = n;
  return n;
}

export function initMetaPixel(): void {
  if (typeof window === 'undefined' || enabled) return;
  const id = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
  if (!id || window.location.hostname !== PROD_HOST) return;
  enabled = true;

  rememberFbclid();
  const fbq = installStub();
  fbq('init', id);
  fbq('track', 'PageView');

  // Loaded right away (async), not on idle: the pixel turns an ad's
  // ?fbclid= into the `_fbc` cookie, and the router may drop the query
  // string within the first second.
  const s = document.createElement('script');
  s.async = true;
  s.src = SCRIPT_URL;
  // A blocked pixel must never surface to the learner; the stub just keeps
  // queueing into nothing.
  s.onerror = () => {};
  document.head.appendChild(s);
}

function rememberFbclid(): void {
  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) localStorage.setItem(FBC_KEY, `fb.1.${Date.now()}.${fbclid}`);
  } catch {
    // Private mode. The cookie path still works.
  }
}

function readCookie(name: string): string | undefined {
  const hit = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : undefined;
}

/**
 * Extra Paddle custom_data, so the webhook's Conversions API event can be
 * matched to the ad click. Empty when the pixel is off.
 */
export function metaCheckoutData(): Record<string, string> {
  if (!enabled) return {};
  const out: Record<string, string> = { ua: navigator.userAgent.slice(0, 512) };
  const fbp = readCookie('_fbp');
  let fbc = readCookie('_fbc');
  if (!fbc) {
    try {
      fbc = localStorage.getItem(FBC_KEY) ?? undefined;
    } catch {
      // ignore
    }
  }
  if (fbp) out.fbp = fbp;
  if (fbc) out.fbc = fbc;
  return out;
}

/**
 * Call on every SIGNED_IN. Fires CompleteRegistration once per account, and
 * only while the account is new — a returning user signing in on a new phone
 * is not a registration.
 */
export function trackMetaRegistration(userId: string, createdAt: string | undefined): void {
  if (!enabled || !window.fbq || !createdAt) return;
  const age = Date.now() - Date.parse(createdAt);
  if (!(age >= 0 && age < NEW_ACCOUNT_MS)) return;
  try {
    const seen = (localStorage.getItem(REG_KEY) ?? '').split(',');
    if (seen.includes(userId)) return;
    localStorage.setItem(REG_KEY, [...seen.filter(Boolean).slice(-9), userId].join(','));
  } catch {
    // No storage: still report; Meta tolerates a rare double.
  }
  window.fbq('track', 'CompleteRegistration');
}

/** The parts of Paddle.js's checkout.completed payload we read. */
export interface CheckoutCompletedData {
  transaction_id?: string;
  currency_code?: string;
  totals?: { total?: number };
}

/**
 * A new subscription went through. $0 = the 7-day trial started; anything
 * else = money was taken now (resubscribing skips the trial).
 */
export function trackMetaCheckout(data: CheckoutCompletedData | undefined): void {
  if (!enabled || !window.fbq || !data?.transaction_id) return;
  const total = data.totals?.total;
  if (typeof total !== 'number' || !Number.isFinite(total)) return;
  const currency = (data.currency_code ?? 'USD').toUpperCase();
  if (total === 0) {
    window.fbq('track', 'StartTrial', { currency }, { eventID: `trial_${data.transaction_id}` });
  } else {
    window.fbq('track', 'Purchase', { currency, value: total }, { eventID: `purchase_${data.transaction_id}` });
  }
}
